import {
  createParser,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsJson,
  parseAsString,
  parseAsStringLiteral,
} from 'nuqs';

import { TIME_WINDOWS, type TimeWindow } from '../_shared/time-window';
import { PROJECT_WORK_ORDER_STATUS_FILTERS } from './project-status-filter';
import { type ProjectPlanningListItem, projectStatusValues } from './types';

/**
 * The Project Plan's time window, on the shared vocabulary (ENG-918).
 *
 * `everything` was this page's own word for Anytime before the two planners
 * were unified. It is still accepted on the way IN so links, bookmarks and
 * tabs opened before the change keep working — it is never written back out.
 */
const LEGACY_ANYTIME = 'everything';

const parseAsTimeWindow = createParser<TimeWindow>({
  parse: (value) => {
    if (value === LEGACY_ANYTIME) return 'anytime';
    return (TIME_WINDOWS as readonly string[]).includes(value) ? (value as TimeWindow) : null;
  },
  serialize: (value) => value,
});

/** nuqs parsers for project planning list URL search params. */
export const projectSearchParsers = {
  /** Free text search across project name and factory name */
  q: parseAsString,
  /** Filter by project status (draft, released) */
  status: parseAsStringLiteral(projectStatusValues),
  /** ENG-981: archived projects are hidden unless this is on. */
  archived: parseAsBoolean.withDefault(false),
};

/**
 * nuqs parsers shared by the project Redraft / Release confirmation
 * sub-routes. The planner toolbar's "Redraft N work orders" /
 * "Release N work orders" buttons pass the eligible subset via this `ids`
 * param; the stepper's whole-project links drop it.
 */
export const projectActionSearchParsers = {
  /** Comma-separated work_order_ids; empty array = whole-project flow. */
  ids: parseAsArrayOf(parseAsString).withDefault([]),
};

/**
 * AG Grid's filter model is `Record<columnId, unknown>`. We treat it as
 * opaque JSON — the grid ignores malformed entries via setFilterModel, so we
 * only need to guard against non-object URL values.
 */
type ProjectFilterModel = Record<string, unknown>;

const parseProjectFilterModel = (v: unknown): ProjectFilterModel | null => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  return v as ProjectFilterModel;
};

/** Time Window state shared by the project-plan controls and editor row scope. */
export const projectPlanSearchParsers = {
  /** Project Plan table + summary scope. Anytime deliberately includes undated work orders. */
  time_window: parseAsTimeWindow.withDefault('anytime'),
  /** Calendar anchor for the selected Time Window. Null while viewing Anytime. */
  period: parseAsString,
};

/** nuqs parsers for the planning grid (single-project) page. */
export const projectEditorSearchParsers = {
  filters: parseAsJson(parseProjectFilterModel).withDefault({}),
  ...projectPlanSearchParsers,
  /**
   * Band 2's work-order status multi-select (ENG-720). Empty = show every
   * loaded status, drafts included — see `matchesProjectStatusFilter`. Named
   * `wo_status` rather than `status` so it can't collide with the project
   * *list* page's `status` param in a shared link.
   */
  wo_status: parseAsArrayOf(parseAsStringLiteral(PROJECT_WORK_ORDER_STATUS_FILTERS)).withDefault(
    [],
  ),
  /**
   * When true, the planner fetches + shows archived work orders (greyed,
   * read-only) so they can be selected and unarchived. Drives the
   * `include_archived` query param on the detail fetch.
   */
  show_archived: parseAsBoolean.withDefault(false),
};

/**
 * Client-side filter on already-fetched project list items.
 * Matches text on project_name or any included factory name (case insensitive),
 * optionally filters by status, and hides archived projects unless
 * `showArchived` (the list endpoint returns them all).
 */
export function filterProjects(
  items: ProjectPlanningListItem[],
  q: string | null,
  status: string | null,
  showArchived: boolean,
): ProjectPlanningListItem[] {
  return items.filter((p) => {
    if (!showArchived && p.is_archived) return false;
    if (status && p.project_status !== status) return false;

    if (q) {
      const lower = q.toLowerCase();
      const nameMatch = p.project_name.toLowerCase().includes(lower);
      const codeMatch = p.project_code.toLowerCase().includes(lower);
      const factoryMatch = p.factory_names.some((name) => name.toLowerCase().includes(lower));
      if (!nameMatch && !codeMatch && !factoryMatch) return false;
    }

    return true;
  });
}
